import cookies from 'js-cookie';

const COOKIE_KEY = 'aff_lt';

export const MEDIUM = 'utm_medium';
export const CAMPAIGN = 'utm_campaign';
export const SOURCE = 'utm_source';
export const AP = 'ap';
export const AID = 'aid';
export const GCLID = 'gclid';
export const FBCLID = 'fbclid';
export const GBRAID = 'gbraid';
export const WBRAID = 'wbraid';

// "Last-click" marketing signals: when a fresh URL carries any of these, it
// represents a new attribution touch (a new ad click, a new campaign link)
// and fully replaces the previously captured set of these fields - it does
// NOT get merged field-by-field with the old values. That avoids laundering
// a new click through stale, mismatched values from an unrelated earlier
// touch (e.g. a bare `fbclid` land should never inherit a stale
// `utm_source=google`/`utm_campaign` from a prior, different click).
const MARKETING_PARAMS = [MEDIUM, CAMPAIGN, SOURCE, GCLID, FBCLID, GBRAID, WBRAID];

// Independent signals: not part of the last-click marketing set above.
// `ap`/`aid` identify an affiliate/partner referral; a bare `ap`/`aid` land
// (no UTMs) should not wipe out previously captured UTM attribution, and a
// fresh marketing touch (above) should not wipe out a previously captured
// `ap`/`aid` either. These are simply overlaid with whatever is most recent.
const INDEPENDENT_PARAMS = [AP, AID];

// Attribution parameters to capture and persist
const ATTRIBUTION_PARAMS = [...MARKETING_PARAMS, ...INDEPENDENT_PARAMS];

// TEMPORARY STOPGAP - remove once a real, durable affiliate/partner
// attribution mechanism exists (tracked separately). Some partners
// identify themselves via utm_source instead of ap/aid (e.g. rackline sends
// utm_source=rackline, utm_medium=app), which puts their attribution in the
// last-click MARKETING_PARAMS group above and makes it vulnerable to being
// wiped by an unrelated later touch (a paid ad, another campaign link)
// before signup. As of 2026-09, rackline is the ONLY partner tracked this
// way - the team has agreed not to onboard additional partners like this
// until a proper affiliate tracking system replaces this allowlist.
const KNOWN_AFFILIATE_UTM_SOURCES = ['rackline'];

const isKnownAffiliateSource = value =>
  !!value && KNOWN_AFFILIATE_UTM_SOURCES.includes(value.toLowerCase());

const getUtmParams = params => {
  const newParams = new URLSearchParams();

  params.forEach((value, key) => {
    if (ATTRIBUTION_PARAMS.indexOf(key) > -1) {
      newParams.set(key, value);
    }
  });

  return newParams;
};

const parseCookie = affCookie => {
  if (!affCookie || !affCookie.length) {
    return new URLSearchParams();
  }

  try {
    return getUtmParams(new URLSearchParams(atob(affCookie)));
  } catch (e) {
    // Corrupted/legacy cookie value - treat as if nothing was stored.
    return new URLSearchParams();
  }
};

const affiliate = {
  loadParams() {
    const cookieParams = parseCookie(cookies.get(COOKIE_KEY));
    const urlParams = getUtmParams(new URLSearchParams(document.location.search));

    const isNewMarketingTouch = MARKETING_PARAMS.some(key => urlParams.has(key));
    const urlIsKnownAffiliate = isKnownAffiliateSource(urlParams.get(SOURCE));
    const cookieIsKnownAffiliate = isKnownAffiliateSource(cookieParams.get(SOURCE));

    const params = new URLSearchParams();

    if (isNewMarketingTouch && (urlIsKnownAffiliate || !cookieIsKnownAffiliate)) {
      // Fresh last-click touch: take the marketing fields exactly as given
      // by the URL, full stop - fields it doesn't mention are left unset
      // rather than backfilled from the old cookie.
      // Exception (temporary stopgap, see KNOWN_AFFILIATE_UTM_SOURCES
      // above): if we already have a known affiliate's attribution stored
      // and this new touch is NOT from a known affiliate, don't let it
      // displace the affiliate credit - fall through to the "keep what we
      // had" branch below instead.
      MARKETING_PARAMS.forEach(key => {
        if (urlParams.has(key)) {
          params.set(key, urlParams.get(key));
        }
      });
    } else {
      // No new marketing touch on this page view (e.g. a client-routed page
      // with no query string), OR an unrelated non-affiliate touch landed
      // while a known affiliate's attribution was already captured - keep
      // whatever marketing attribution was already captured.
      MARKETING_PARAMS.forEach(key => {
        if (cookieParams.has(key)) {
          params.set(key, cookieParams.get(key));
        }
      });
    }

    // Independent signals always just overlay - the URL's value wins if
    // present, otherwise fall back to what was already captured. These
    // never trigger, and are never cleared by, a marketing-group reset.
    INDEPENDENT_PARAMS.forEach(key => {
      const value = urlParams.has(key) ? urlParams.get(key) : cookieParams.get(key);
      if (value) {
        params.set(key, value);
      }
    });

    if (!params.toString().length) {
      return params;
    }

    return this.saveParams(params);
  },

  saveParams(params) {
    // Set cookie with appropriate flags to survive OAuth redirects
    // SameSite=Lax allows cookie to be sent on top-level navigations (including OAuth returns)
    // Secure flag is set only when using HTTPS (production)
    const isSecure = window.location.protocol === 'https:';

    cookies.set(COOKIE_KEY, btoa(params.toString()), {
      expires: 90,
      sameSite: 'Lax',
      ...(isSecure && { secure: true }),
    });

    return params;
  },

  has(key) {
    return this.loadParams().has(key);
  },

  medium(defaultValue = null) {
    return this.get(MEDIUM, defaultValue);
  },

  campaign(defaultValue = null) {
    return this.get(CAMPAIGN, defaultValue);
  },

  source(defaultValue = null) {
    return this.get(SOURCE, defaultValue);
  },

  ap(defaultValue = null) {
    return this.get(AP, defaultValue);
  },

  aid(defaultValue = null) {
    return this.get(AID, defaultValue);
  },

  gclid(defaultValue = null) {
    return this.get(GCLID, defaultValue);
  },

  fbclid(defaultValue = null) {
    return this.get(FBCLID, defaultValue);
  },

  gbraid(defaultValue = null) {
    return this.get(GBRAID, defaultValue);
  },

  wbraid(defaultValue = null) {
    return this.get(WBRAID, defaultValue);
  },

  get(key, defaultValue = null) {
    const value = this.loadParams().get(key);

    return value || defaultValue;
  },

  all() {
    return {
      [MEDIUM]: this.medium(),
      [CAMPAIGN]: this.campaign(),
      [SOURCE]: this.source(),
      [AP]: this.ap(),
      [AID]: this.aid(),
      [GCLID]: this.gclid(),
      [FBCLID]: this.fbclid(),
      [GBRAID]: this.gbraid(),
      [WBRAID]: this.wbraid(),
    };
  },
};

export default affiliate;
